import { type ReactNode, useRef } from 'react'
import { ALLOWED_ATTACHMENT_MIME_TYPES, isEmpty } from '@terros/common'
import { Dropzone } from '@mantine/dropzone'
import { Group, Stack, Text } from '@mantine/core'
import { SizePrimitives } from '@front/model'
import { MaterialSymbolIcon } from '../terros/icon/MaterialSymbolIcon'
import { TButton } from '../buttons'

const MB = 1024 ** 2
export const MAX_ATTACHMENT_SIZE_BYTES = 20 * MB
export const MAX_ATTACHMENT_SIZE_LABEL = '20MB'

type AttachmentDropzoneProps = {
  multiple?: boolean
  loading: boolean
  error: string | null
  onDrop: (files: File[]) => void
}

export const AttachmentDropzone = ({
  multiple = false,
  loading,
  error,
  onDrop,
}: AttachmentDropzoneProps): ReactNode => {
  const openRef = useRef<() => void>(null)

  const handleDrop = (files: File[]): void => {
    if (isEmpty(files)) return
    onDrop(files)
  }

  return (
    <Dropzone
      loading={loading}
      onDrop={handleDrop}
      maxSize={MAX_ATTACHMENT_SIZE_BYTES}
      maxFiles={multiple ? undefined : 1}
      accept={ALLOWED_ATTACHMENT_MIME_TYPES}
      openRef={openRef}
    >
      <Group justify='start' gap='md' style={{ pointerEvents: 'none' }}>
        <Dropzone.Accept>
          <MaterialSymbolIcon icon='upload' color='blue' size={SizePrimitives['12']} />
        </Dropzone.Accept>
        <Dropzone.Reject>
          <MaterialSymbolIcon icon='close' color='red' size={SizePrimitives['12']} />
        </Dropzone.Reject>
        <Dropzone.Idle>
          <MaterialSymbolIcon icon={multiple ? 'attach_file' : 'image'} color='dimmed' size={SizePrimitives['12']} />
        </Dropzone.Idle>
        <Stack gap='xs'>
          <Text inline>
            {multiple
              ? 'Drag attachments here or click to select files'
              : 'Drag attachment here or click to select file'}
          </Text>
          <Text size='xs' c='dimmed'>
            {multiple
              ? `Attach one or more files, each should not exceed ${MAX_ATTACHMENT_SIZE_LABEL}`
              : `Attach a file, the attachment should not exceed ${MAX_ATTACHMENT_SIZE_LABEL}`}
          </Text>
          <div>
            <TButton onClick={() => openRef.current?.()} style={{ pointerEvents: 'all' }}>
              {multiple ? 'Select attachments' : 'Select attachment'}
            </TButton>
          </div>
          {error && (
            <Text size='xs' c='red'>
              {error}
            </Text>
          )}
        </Stack>
      </Group>
    </Dropzone>
  )
}
